import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import logoImage from "@assets/legacyloop-white-trans.png";
import { useAuth } from "@/hooks/useAuth";

const Footer = () => {
  const [, navigate] = useLocation();
  const { isAuthenticated } = useAuth();

  // Logged-in users go to the real page; new visitors go to sign-up
  const featureHref = (path: string) =>
    isAuthenticated ? path : "/login?mode=signup";
  
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  
  return (
    <footer className="bg-[#0D1B2A] text-white border-t border-[#1B263B]">
      <div className="container mx-auto px-4 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <img
                src={logoImage}
                alt="LegacyLoop"
                className="h-36 w-auto"
              />
            </div>
            <p className="text-neutral-300 text-sm">
              Preserve the Journey. Relive Every Moment.
            </p>
          </div>

          <div>
            <h3 className="font-montserrat font-semibold mb-4">Features</h3>
            <ul className="space-y-2 text-neutral-300 text-sm">
              <li>
                <a href={featureHref("/dashboard")} className="hover:text-[#E0A458] transition cursor-pointer">
                  Athlete Dashboard
                </a>
              </li>
              <li>
                <a href={featureHref("/journal")} className="hover:text-[#E0A458] transition cursor-pointer">
                  Smart Journaling
                </a>
              </li>
              <li>
                <a href={featureHref("/media")} className="hover:text-[#E0A458] transition cursor-pointer">
                  Media Library
                </a>
              </li>
              <li>
                <a href={featureHref("/family")} className="hover:text-[#E0A458] transition cursor-pointer">
                  The Loop
                </a>
              </li>
              <li>
                <a href={featureHref("/recruiting")} className="hover:text-[#E0A458] transition cursor-pointer">
                  Recruiting Tools
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-montserrat font-semibold mb-4">Support</h3>
            <ul className="space-y-2 text-neutral-300 text-sm">
              <li>
                <a 
                  href="/help"
                  className="hover:text-[#E0A458] transition cursor-pointer"
                >
                  Help Center
                </a>
              </li>
              <li>
                <a 
                  href="/contact"
                  className="hover:text-[#E0A458] transition cursor-pointer"
                >
                  Contact Us
                </a>
              </li>
              <li>
                <a 
                  href="/privacy"
                  className="hover:text-[#E0A458] transition cursor-pointer"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a 
                  href="/terms"
                  className="hover:text-[#E0A458] transition cursor-pointer"
                >
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-montserrat font-semibold mb-4">Join Our Newsletter</h3>
            <p className="text-neutral-300 text-sm mb-4">
              Be the first to know about new features and early access invites.
            </p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const email = (e.currentTarget.elements.namedItem('email') as HTMLInputElement).value;
              if (!email) return;
              try {
                const res = await fetch('/api/newsletter/subscribe', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email }),
                });
                if (res.ok) {
                  alert('Thanks! Check your inbox for a confirmation.');
                  (e.target as HTMLFormElement).reset();
                } else {
                  const data = await res.json().catch(() => ({}));
                  alert(data.message || 'Something went wrong. Please try again.');
                }
              } catch {
                alert('Network error. Please try again.');
              }
            }}>
              <div className="flex">
                <Input 
                  name="email"
                  type="email" 
                  placeholder="Enter your email" 
                  className="rounded-l-lg w-full bg-white text-neutral-800 placeholder:text-neutral-400 text-sm rounded-r-none"
                  required
                />
                <Button 
                  type="submit"
                  className="bg-[#E0A458] hover:bg-[#f0b467] text-black rounded-l-none whitespace-nowrap px-3"
                >
                  Subscribe
                </Button>
              </div>
            </form>
          </div>
        </div>

        <div className="border-t border-neutral-700 mt-8 pt-6 text-center text-neutral-400 text-sm">
          <p>&copy; {new Date().getFullYear()} LegacyLoop. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
